import * as React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';

import { ContentSpacing } from '@estee/elc-base-theme';
import { configConsumer } from '@estee/elc-service';

import { classes } from '../utils/Classes';
import { VtoShadeGridContainerTheme } from '../../theme/default-theme';
import { IProductImage, IShadePicker, IVtoFeature } from '../interfaces/IVto';
import { VtoFoundationCustomShadePicker } from './vto-foundation/VtoFoundationCustomShadePicker';
import { VtoPairShadePicker } from './vto-foundation/VtoPairShadePicker';
import { VtoViewController } from '../controllers/VtoViewController';
import { VtoShadeInfoContainer } from './vto-foundation/VtoShadeInfoContainer';
import { VtoGridTabs } from './vto-foundation/VtoGridTabs';

export interface IVtoFoundationShadeGridProps {
    saveMatches(): void;
    removeMatches(): void;
    isMatchSaved: boolean;
    isSaveInProgress: boolean;
    isVtoPriceHidden: boolean;
    config?: IVtoFoundationShadeGridConfig;
    productShadePickerProps: IShadePicker;
    togglePerfectShades(isEnabled: boolean): void;
    largeImages: IProductImage[];
    selectedAutoReplenishOption: number;
    viewController: VtoViewController;
}

interface IVtoFoundationShadeGridConfig {
    vtoFeature: IVtoFeature;
}

const Wrapper = styled.div`
    position: absolute;
    bottom: 0;
    padding: ${ContentSpacing.space10} 0 ${ContentSpacing.space20};
    width: 100%;
    z-index: 100;
    visibility: visible;
    & .elc-shade-carousel {
        padding: 0 ${ContentSpacing.space16} ${ContentSpacing.space10};
    }
    & .elc-product-shade-picker-slider .slick-track {
        margin: 0 auto;
    }
    & .elc-product-shade-picker-title {
        display: none;
    }

    ${VtoShadeGridContainerTheme}
`;

const vtoFoundationShadeGridWrapperClassNames = classes('vto-foundation-shade-picker-wrapper');

@configConsumer(['vtoFeature'])
@observer
export class VtoFoundationShadeGrid extends React.Component<IVtoFoundationShadeGridProps> {
    private getCrossSellProductProps() {
        const {
            viewController: {
                data: {
                    selectedCrossSellSku,
                    selectedCrossSellShade,
                    selectedCrossSellProductUrl,
                    selectedCrossSellLargeImages,
                    selectedCrossSellSizeValue
                }
            }
        } = this.props;

        return {
            pairProduct: selectedCrossSellSku?.parentProduct,
            pairProductUrl: selectedCrossSellProductUrl,
            pairProductLargeImages: selectedCrossSellLargeImages,
            selectedPairShade: selectedCrossSellShade,
            selectedPairSku: selectedCrossSellSku,
            selectedPairSizeValue: selectedCrossSellSizeValue
        };
    }

    private getCrossSellShadePickerProps() {
        const {
            viewController: {
                onCrossSellShadeSelect,
                data: { selectedCrossSellShade, selectedCrossSellShades, foundationPairText }
            }
        } = this.props;

        return {
            selectedPairShades: selectedCrossSellShades,
            selectedPairShade: selectedCrossSellShade,
            onPairShadeSelect: onCrossSellShadeSelect,
            vtoFoundationPairText: foundationPairText
        };
    }

    public render() {
        const { config, productShadePickerProps, viewController } = this.props;
        const { onVtoTabClick, sortPerfectLabels, setActiveTab, data } = viewController;
        const { isPerfectShadeEnabled } = productShadePickerProps;
        const { activeTabs, isMatchSaved, savedMatch } = data;
        const { vtoFeature } = config as IVtoFoundationShadeGridConfig;

        const vtoFoundationFeature = vtoFeature && vtoFeature.foundation;
        const vtoFoundationShadeWrapperClassNames = classes(
            'vto-foundation-shade-grid',
            { 'vto-foundation-perfect-shade': isPerfectShadeEnabled },
            { 'vto-foundation-lip': vtoFoundationFeature && vtoFoundationFeature.hasLipVto }
        );

        const pairShadePickerProps = this.getCrossSellShadePickerProps();
        const pairProductData = this.getCrossSellProductProps();
        const isPairShadesTabActive = activeTabs.pairShades.active;
        const isAllShadesTabActive = activeTabs.allShades.active;

        return (
            <Wrapper className={vtoFoundationShadeWrapperClassNames}>
                <VtoGridTabs onTabClick={onVtoTabClick} tabs={activeTabs} />
                <div className={vtoFoundationShadeGridWrapperClassNames}>
                    <VtoFoundationCustomShadePicker
                        tabs={activeTabs}
                        vtoFoundationFeature={vtoFoundationFeature}
                        productShadePickerProps={productShadePickerProps}
                        sortPerfectLabels={sortPerfectLabels}
                        isMatchSaved={isMatchSaved}
                        savedMatch={savedMatch}
                    />
                    {isPairShadesTabActive && <VtoPairShadePicker {...pairShadePickerProps} />}
                </div>
                <VtoShadeInfoContainer
                    {...this.props}
                    isPairShadesTabActive={isPairShadesTabActive}
                    pairProductData={pairProductData}
                    setActiveTab={setActiveTab}
                    isAllShadesTabActive={isAllShadesTabActive}
                />
            </Wrapper>
        );
    }
}
